// One-time migration (CYBORG-1560): an older app parked messages for a busy agent in localStorage
// (cyborg7.queuedPrompts.<agentId>). The queue is the daemon's now, so each one is sent there, in
// order, and the key is removed. Delete this module in a later release.
import { untrack } from "svelte";
import { z } from "zod";
import { reportUiFailure, reportUiKpi } from "../../report-failure.js";
import type { AgentCommandInput } from "./agent-command-input.js";
import type { Attachment } from "../../types.js";
import type { PageCard } from "../../core/types.js";

export const LEGACY_QUEUE_PREFIX = "cyborg7.queuedPrompts.";

const LegacyRowSchema = z.object({
  text: z.string(),
  command: z.object({ name: z.string(), args: z.string() }).optional(),
  attachments: z.array(z.custom<Attachment>((v) => typeof v === "object" && v !== null)).optional(),
  pageRef: z.string().optional(),
  pageCard: z.custom<PageCard>((v) => typeof v === "object" && v !== null).optional(),
  workSessionId: z.string().optional(),
  // Set: the old app had already handed it to the daemon, which may have run it.
  inFlightPromptId: z.string().optional(),
});
const LegacyQueueSchema = z.object({ prompts: z.array(LegacyRowSchema) });

export type LegacyRow = z.infer<typeof LegacyRowSchema>;

export type LegacyStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface LegacyQueueDeps {
  storage: LegacyStorage;
  // A normal send under `promptId`: draws its bubble, which a refusal leaves failed with Retry.
  sendPrompt(agentId: string, row: LegacyRow, promptId: string): Promise<void>;
  sendCommand(
    agentId: string,
    command: AgentCommandInput,
    promptId: string,
    workSessionId?: string,
  ): Promise<void>;
  // A command the daemon refused: drawn failed, its Retry resends it as the command.
  drawFailedCommand(
    agentId: string,
    row: LegacyRow,
    command: AgentCommandInput,
    promptId: string,
  ): void;
  newPromptId(): string;
  // Already with the daemon once: shown as delivery unknown, never sent a second time.
  drawUnconfirmed(agentId: string, row: LegacyRow, promptId: string): void;
}

export interface LegacyQueueCounts {
  sent: number;
  failed: number;
  unconfirmed: number;
}

function readQueue(deps: LegacyQueueDeps, key: string): LegacyRow[] | undefined {
  const raw = deps.storage.getItem(key);
  if (raw === null) return undefined;
  try {
    return LegacyQueueSchema.parse(JSON.parse(raw)).prompts;
  } catch (err) {
    deps.storage.removeItem(key);
    reportUiFailure("agents.legacy_queue_unreadable", err, { failure_kind: "fault" });
    return undefined;
  }
}

async function sendOne(
  deps: LegacyQueueDeps,
  agentId: string,
  row: LegacyRow,
  promptId: string,
): Promise<boolean> {
  try {
    if (row.command) await deps.sendCommand(agentId, row.command, promptId, row.workSessionId);
    else await deps.sendPrompt(agentId, row, promptId);
    return true;
  } catch (err) {
    if (row.command) deps.drawFailedCommand(agentId, row, row.command, promptId);
    reportUiFailure("agents.legacy_queue_send", err, { agentId });
    return false;
  }
}

// Sends this agent's legacy queue in order. Before each send the key marks that row with its
// prompt id, so a reload mid-way neither loses it nor sends it twice. undefined: no legacy queue.
export async function migrateLegacyQueue(
  agentId: string,
  deps: LegacyQueueDeps,
): Promise<LegacyQueueCounts | undefined> {
  const key = `${LEGACY_QUEUE_PREFIX}${agentId}`;
  const rows = readQueue(deps, key);
  if (!rows) return undefined;
  const counts: LegacyQueueCounts = { sent: 0, failed: 0, unconfirmed: 0 };
  const keep = (left: LegacyRow[]): void => {
    if (left.length > 0) deps.storage.setItem(key, JSON.stringify({ prompts: left }));
    else deps.storage.removeItem(key);
  };
  for (const [at, row] of rows.entries()) {
    if (row.inFlightPromptId) {
      keep(rows.slice(at + 1));
      deps.drawUnconfirmed(agentId, row, row.inFlightPromptId);
      counts.unconfirmed += 1;
      continue;
    }
    const promptId = deps.newPromptId();
    keep([{ ...row, inFlightPromptId: promptId }, ...rows.slice(at + 1)]);
    const sent = await sendOne(deps, agentId, row, promptId);
    keep(rows.slice(at + 1));
    if (sent) counts.sent += 1;
    else counts.failed += 1;
  }
  reportUiKpi("agents.legacy_queue_migrated", { agentId, ...counts });
  return counts;
}

// Starts the migrations the listed agents need, one at a time per agent.
function startMigrations(
  listed: readonly { agentId: string }[],
  deps: LegacyQueueDeps,
  running: Set<string>,
): void {
  for (const { agentId } of listed) {
    const key = `${LEGACY_QUEUE_PREFIX}${agentId}`;
    if (running.has(agentId) || deps.storage.getItem(key) === null) continue;
    running.add(agentId);
    migrateLegacyQueue(agentId, deps)
      .catch((err: unknown) => reportUiFailure("agents.legacy_queue_migration", err, { agentId }))
      .finally(() => running.delete(agentId));
  }
}

// Migrates each listed agent's legacy queue once it is in the roster (its workspace is the open one).
export function followLegacyQueues(
  agents: () => readonly { agentId: string }[],
  ready: () => boolean,
  storage: () => LegacyStorage | undefined, // undefined: no localStorage here (a test, a worker)
  rest: Omit<LegacyQueueDeps, "storage">,
): () => void {
  const running = new Set<string>();
  return $effect.root(() => {
    $effect(() => {
      const listed = agents();
      const store = storage();
      if (!ready() || !store) return;
      const deps: LegacyQueueDeps = { ...rest, storage: store };
      untrack(() => startMigrations(listed, deps, running));
    });
  });
}
